import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal, Typography, Button } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import { IUserPage } from 'src/app/shared/models/userPages.model'
import { colorPrimary } from 'src/app/themes/primaryThemeDefault'
import { InfoIcon, UnsaveAllIcon } from 'src/assets/icons'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import DNDItem from './DNDItem'
import './FavoriteConfirmationModal.scss'

const reorder = (list: any, startIndex: any, endIndex: any) => {
  const result = Array.from(list)
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)

  return result
}

const move = (source: any, destination: any, droppableSource: any, droppableDestination: any) => {
  const sourceClone = Array.from(source)
  const destClone = Array.from(destination)
  const [removed] = sourceClone.splice(droppableSource.index, 1)

  destClone.splice(droppableDestination.index, 0, removed)

  const result: any = {}
  result[droppableSource.droppableId] = sourceClone
  result[droppableDestination.droppableId] = destClone

  return result
}
const grid = 8

const getItemStyle = (isDragging: any, draggableStyle: any) => ({
  userSelect: 'none',
  margin: `0 0 ${grid}px 0`,
  background: '#F3F6F9',
  borderRadius: '12px',
  ...draggableStyle,
})
const getListStyle = (isDraggingOver: any) => ({
  background: '#F3F6F9',
  borderRadius: 12,
  height: 344,
  gap: 8,
  'overflow-y': 'scroll',
  'overflow-x': 'hidden',
})

type FavoriteConfirmationModal = {
  setIsFavoriteConfirmationModalOpen: (value: boolean) => void
  setIsUnsaveAllFavoritePagesModalOpen: (value: boolean) => void
  favoriteList: IUserPage[]
  nonFavoriteList: IUserPage[]
  setFavoriteList: any
  handleSaveFavoriteChanges: any
  isFavoriteConfirmationModalOpen: any
  handleFavoriteConfirmationCancel: any
  handleDiscardUnsavedFavoriteChanges: any
  isLoading: boolean
}

export default function FavoriteConfirmationModal({
  favoriteList = [],
  nonFavoriteList = [],
  handleSaveFavoriteChanges,
  isFavoriteConfirmationModalOpen,
  handleFavoriteConfirmationCancel,
  handleDiscardUnsavedFavoriteChanges,
  setIsFavoriteConfirmationModalOpen,
  setIsUnsaveAllFavoritePagesModalOpen,
  isLoading,
}: Readonly<FavoriteConfirmationModal>) {
  const [state, setState] = useState([[...nonFavoriteList], [...favoriteList]])
  const { t } = useTranslation()
  useEffect(() => {
    setState([[...nonFavoriteList], [...favoriteList]])
  }, [nonFavoriteList, favoriteList])

  function onDragEnd(result: any) {
    const { source, destination } = result

    if (!destination) {
      return
    }
    const sInd = +source.droppableId
    const dInd = +destination.droppableId

    if (sInd === dInd) {
      const items = reorder(state[sInd], source.index, destination.index)
      const newState: any = [...state]
      newState[sInd] = items
      setState(newState)
    } else {
      const result = move(state[sInd], state[dInd], source, destination)
      const newState = [...state]
      newState[sInd] = result[sInd]
      newState[dInd] = result[dInd]

      setState(newState)
    }
  }

  const generateDraggableItem = (elements: IUserPage[]) => {
    return elements.map((item, index) => (
      <Draggable key={item.id} draggableId={item.id} index={index}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
          >
            <DNDItem item={item} />
          </div>
        )}
      </Draggable>
    ))
  }

  const dropableItem = (index: number, elements: IUserPage[]) => {
    return (
      <Droppable key={index} droppableId={`${index}`}>
        {(provided, snapshot) => (
          <div ref={provided.innerRef} style={getListStyle(snapshot.isDraggingOver)} {...provided.droppableProps}>
            {generateDraggableItem(elements)}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    )
  }

  return (
    <Modal
      className='modal-favorite'
      closeIcon={false}
      open={isFavoriteConfirmationModalOpen}
      width={709}
      footer={[
        <Button
          key='cancel-btn'
          style={{
            fontWeight: 600,
          }}
          onClick={handleFavoriteConfirmationCancel}
        >
          {t('OverallFunctionality:cancel_btn')}
        </Button>,
        <Button
          key='discard-btn'
          disabled={JSON.stringify(state[1]) === JSON.stringify(favoriteList) || isLoading}
          onClick={handleDiscardUnsavedFavoriteChanges}
        >
          {t('OverallFunctionality:discard_unsaved_changes_btn')}
        </Button>,
        <Button
          key='save-btn'
          type='primary'
          style={{
            fontWeight: 600,
            fontSize: '14px',
          }}
          loading={isLoading}
          disabled={JSON.stringify(state[1]) === JSON.stringify(favoriteList) || isLoading}
          onClick={() => handleSaveFavoriteChanges(state[1])}
        >
          {t('OverallFunctionality:save_changes_btn')}
        </Button>,
      ]}
    >
      <div>
        <div className='favorite-container'>
          <div className='favorite-header'>
            <Typography.Text style={{ fontSize: '22px', fontWeight: 600 }}>Confirmation</Typography.Text>
            <CloseOutlined
              onClick={() => {
                handleDiscardUnsavedFavoriteChanges()
                setIsFavoriteConfirmationModalOpen(false)
              }}
            />
          </div>
          <div className='favorite-content'>
            <div className='content-header'>
              <InfoIcon height={30} width={30} />
              <div style={{ marginLeft: '15px' }}>
                <Typography.Text style={{ fontSize: '14px' }}>
                  {t('OverallFunctionality:favorite_confirmation_modal_instruction_text')}
                </Typography.Text>
              </div>
            </div>
            <div className='content-body'>
              <DragDropContext onDragEnd={onDragEnd}>
                {state.map((el, ind) => (
                  <div key={ind} className='actions-container'>
                    <div className='action-header'>
                      {ind === 1 ? 'Favorite Actions' : 'All Actions'}
                      {ind === 1 ? (
                        <UnsaveAllIcon
                          onClick={() => {
                            if (el.length > 0) {
                              setIsUnsaveAllFavoritePagesModalOpen(true)
                              setIsFavoriteConfirmationModalOpen(false)
                            } else return
                          }}
                          style={{ cursor: el.length === 0 ? 'not-allowed' : 'pointer' }}
                        />
                      ) : (
                        <></>
                      )}
                    </div>
                    {dropableItem(ind, el)}
                  </div>
                ))}
              </DragDropContext>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}
